// Organization membership and assessment assignments are independent permissions.
export type OrganizationRole = "User" | "Admin" | "Global Admin" | "Storage Only";
export const ASSESSMENT_ASSIGNMENT_ROLES = ["isa", "qsa", "qa", "editors", "readers", "merchant_rep"] as const;
export type AssessmentAssignmentRole = typeof ASSESSMENT_ASSIGNMENT_ROLES[number];

// Local modal identity only. Never serialize this object into an API request.
export interface UserAccessLaunchIdentity {
    accountId: number;
    productLicenseId: number | null;
    targetUserId: number | null;
    sessionIdentity: string;
    revision: number;
}

export interface UserManagementCapabilities {
    canEditProfile: boolean;
    canManageAccount: boolean;
    canManageAssessmentAssignments: boolean;
    canResendInvitation: boolean;
}

export interface UserAccessCapabilities {
    canViewOrganizationUsers: boolean;
    canInviteWithoutAssessment: boolean;
    canInviteOnAssessment: boolean;
    hasEditableAssessments: boolean;
    invitationOrganizationRoles: OrganizationRole[];
}

export interface UserAssessmentSummary {
    productLicenseId: number;
    merchantId: number | null;
    merchantName: string;
    formType: string;
    displayName: string;
    assignmentRoles: AssessmentAssignmentRole[];
    readOnly: boolean;
}

export interface UserAccessContext {
    accountId: number;
    actorUserId: number;
    capabilities: UserAccessCapabilities;
    target: null | {
        userId: number;
        displayName: string;
        organizationRole: OrganizationRole;
        capabilities: UserManagementCapabilities;
        assessmentAssignments: UserAssessmentSummary[];
    };
    assessment: null | (UserAssessmentSummary & {
        allowedRoleChoices: AssessmentAssignmentRole[];
        primaryHolders: { role: AssessmentAssignmentRole; userId: number | null; displayName: string | null }[];
        roleConflicts: { role: AssessmentAssignmentRole; code: string; conflictingRole: AssessmentAssignmentRole | null }[];
    });
}

export interface UserAccessQuery { productLicenseId?: number; targetUserId?: number; }
export interface AssessmentAssignmentResult {
    accountId: number;
    productLicenseId: number;
    targetUserId: number;
    assignmentRoles: AssessmentAssignmentRole[];
    changed: boolean;
    actorRetainsAssessmentAccess: boolean;
}

export interface AccessUserIdentity {
    id: number;
    accountId: number;
    displayName: string;
    email: string;
    role: OrganizationRole;
}
export interface AssessmentInvitationPayload {
    displayName: string;
    email: string;
    role: Exclude<OrganizationRole, "Storage Only">;
    phoneNumber?: string | null;
    certificateNumber?: string | null;
    productLicenseId?: number;
    desiredRoles?: readonly AssessmentAssignmentRole[];
    submissionKey: string;
    assignExistingUserId?: number;
}
export interface ExistingMemberOffer {
    conflictType: "existing_same_account_user";
    canAssignToAssessment: boolean;
    productLicenseId: number | null;
    existingUser: Omit<AccessUserIdentity, "email">;
}
export interface StorageOnlyConversionOffer {
    userId: number;
    accountId: number;
    assignedMerchantNames: string[];
}
export interface StorageOnlyConversionResult {
    outcome: "storage_user_converted";
    user: AccessUserIdentity;
}
export interface PersistedInvitationResult {
    outcome: "invited" | "existing_user_assigned" | "delivery_failed" | "delivery_unknown";
    user: AccessUserIdentity;
    accountId: number;
    productLicenseId: number | null;
    assignmentRoles: AssessmentAssignmentRole[];
    duplicate: boolean;
    assignmentChanged: boolean;
    deliveryState: "sent" | "failed" | "pending" | "not_requested";
    recovery: "resend_invitation" | null;
}
// Cancellation is local and never calls an endpoint.
export type InvitationCompletion = PersistedInvitationResult | { outcome: "canceled" };
export interface InvitationResendResult {
    user: AccessUserIdentity;
    resent: boolean;
    tokenReplaced: true;
    deliveryState: "sent" | "failed";
}
